import type { Paths } from '@src/utils/types';
import { createContext, useContext, useEffect, type MutableRefObject, type ReactNode } from 'react';
import type { EntityAdminRecord } from '../../types';
import type { InputsAndDropdownsDrawerOptions } from '../InputsAndDropdownsDrawer/types';

export interface CustomDrawerContextValue<TDrawerRecord extends EntityAdminRecord> {
  form: TDrawerRecord;
  isEditing: boolean;
  onFormUpdate: (field: Paths<TDrawerRecord>, value: unknown) => void;
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>;
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>;
  /** The original entity that was fetched from the server. Undefined for new entities. */
  originalEntity: TDrawerRecord | undefined;
  errorState: Record<string, string | undefined> | null;
  touchState: Record<string, boolean>;
  /** Whether this drawer is for adding a child entity */
  addingChildEntity: boolean;
  /** Register visible tabs for validation filtering */
  setVisibleTabs: (tabs: string[]) => void;
}

// Using 'any' here because React contexts don't work well with generics at creation time.
// The actual type safety is provided by the useCustomDrawer hook which casts to the correct type.
const CustomDrawerContext = createContext<CustomDrawerContextValue<any> | undefined>(undefined);

export function CustomDrawerProvider<TDrawerRecord extends EntityAdminRecord>({
  children,
  value,
}: {
  children: ReactNode;
  value: CustomDrawerContextValue<TDrawerRecord>;
}) {
  return <CustomDrawerContext.Provider value={value}>{children}</CustomDrawerContext.Provider>;
}

export function useCustomDrawer<TDrawerRecord extends EntityAdminRecord>(): CustomDrawerContextValue<TDrawerRecord> {
  const context = useContext(CustomDrawerContext);
  if (!context) {
    throw new Error(
      'useCustomDrawer must be used within a CustomDrawerProvider. Wrap your component with <CustomDrawerProvider>.'
    );
  }
  return context as CustomDrawerContextValue<TDrawerRecord>;
}

const CustomDrawerAdditionalDirtyContext = createContext<((dirty: boolean) => void) | undefined>(undefined);

export function CustomDrawerAdditionalDirtyProvider({
  value,
  children,
}: {
  value: (dirty: boolean) => void;
  children: ReactNode;
}) {
  return (
    <CustomDrawerAdditionalDirtyContext.Provider value={value}>{children}</CustomDrawerAdditionalDirtyContext.Provider>
  );
}

/**
 * Report dirty state that lives outside the EntityAdmin form (slice state, nested editors).
 * Combined with form dirty for leave guards. Must be called from a custom drawer layout.
 */
export function useEntityAdminAdditionalDirty(isDirty: boolean) {
  const setAdditionalDirty = useContext(CustomDrawerAdditionalDirtyContext);
  if (setAdditionalDirty == null) {
    throw new Error('useEntityAdminAdditionalDirty must be used within a CustomDrawer.');
  }
  useEffect(() => {
    setAdditionalDirty(isDirty);
    return () => setAdditionalDirty(false);
  }, [isDirty, setAdditionalDirty]);
}

export type CustomDrawerRebaseFormAfterSave<TDrawerRecord extends EntityAdminRecord> = (
  form: TDrawerRecord
) => TDrawerRecord;

const CustomDrawerRebaseAfterSaveContext = createContext<
  MutableRefObject<CustomDrawerRebaseFormAfterSave<any> | undefined> | undefined
>(undefined);

export function CustomDrawerRebaseAfterSaveProvider({
  value,
  children,
}: {
  value: MutableRefObject<CustomDrawerRebaseFormAfterSave<any> | undefined>;
  children: ReactNode;
}) {
  return (
    <CustomDrawerRebaseAfterSaveContext.Provider value={value}>{children}</CustomDrawerRebaseAfterSaveContext.Provider>
  );
}

/**
 * Normalize form state after a successful save that leaves the drawer open (e.g. permission dirty flags).
 * The rebase runs synchronously before `markFormPristine` snapshots the form.
 */
export function useEntityAdminRebaseFormAfterSave<TDrawerRecord extends EntityAdminRecord>(
  rebase: CustomDrawerRebaseFormAfterSave<TDrawerRecord>
) {
  const rebaseRef = useContext(CustomDrawerRebaseAfterSaveContext);
  if (rebaseRef == null) {
    throw new Error('useEntityAdminRebaseFormAfterSave must be used within a CustomDrawer.');
  }
  useEffect(() => {
    rebaseRef.current = rebase;
    return () => {
      rebaseRef.current = undefined;
    };
  }, [rebase, rebaseRef]);
}
